import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Phone, Mail, MapPin, Package, Calendar, Receipt, IndianRupee } from "lucide-react";

export default function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data || []));
  }, []);

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-semibold text-charcoal">Orders</h2>
        <p className="text-sm text-charcoal/60">{orders.length} total · click any row for details</p>
      </div>
      <div className="bg-white border border-black/10 rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-charcoal/50 py-8">No orders yet</TableCell></TableRow>
            )}
            {orders.map((o) => (
              <TableRow
                key={o.id}
                data-testid={`order-row-${o.id}`}
                onClick={() => setSelected(o)}
                className="cursor-pointer hover:bg-stone/30"
              >
                <TableCell className="font-mono text-xs">{new Date(o.created_at).toLocaleString()}</TableCell>
                <TableCell>{o.customer_name || "—"}</TableCell>
                <TableCell className="text-xs">
                  <div>{o.email}</div>
                  <div className="text-charcoal/60">{o.mobile}</div>
                </TableCell>
                <TableCell className="text-xs max-w-xs truncate">
                  {o.items?.map((i) => `${i.name} (${i.quantity_text})`).join(", ")}
                </TableCell>
                <TableCell className="font-mono">₹{Number(o.total).toFixed(2)}</TableCell>
                <TableCell>
                  <Badge className={o.payment_status === "paid" ? "bg-moss text-white hover:bg-moss" : "bg-stone text-charcoal hover:bg-stone"} style={o.payment_status === "paid" ? { backgroundColor: "var(--moss)" } : {}}>
                    {o.payment_status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="order-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-serif text-2xl text-forest">
                  Order #{selected.id.slice(0, 8)}
                </SheetTitle>
                <SheetDescription className="flex items-center gap-1 font-mono text-xs">
                  <Calendar className="w-3 h-3" /> {new Date(selected.created_at).toLocaleString()}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Status band */}
                <div className="flex items-center justify-between bg-stone/30 rounded-md p-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-charcoal/50">Total</div>
                    <div className="font-serif text-2xl text-forest flex items-center">
                      <IndianRupee className="w-4 h-4" />{Number(selected.total).toFixed(2)}
                    </div>
                  </div>
                  <Badge className={selected.payment_status === "paid" ? "bg-moss text-white hover:bg-moss" : ""} style={selected.payment_status === "paid" ? { backgroundColor: "var(--moss)" } : {}}>
                    {selected.payment_status}
                  </Badge>
                </div>

                {/* Customer */}
                <div>
                  <h3 className="text-xs uppercase tracking-[0.24em] text-charcoal/50 mb-3">Customer</h3>
                  <div className="space-y-2 text-sm">
                    {selected.customer_name && (
                      <div className="font-medium text-charcoal">{selected.customer_name}</div>
                    )}
                    <div className="flex items-center gap-2 text-charcoal/80">
                      <Mail className="w-3.5 h-3.5 text-forest" />
                      <a href={`mailto:${selected.email}`} className="hover:text-forest">{selected.email}</a>
                    </div>
                    <div className="flex items-center gap-2 text-charcoal/80">
                      <Phone className="w-3.5 h-3.5 text-forest" />
                      <a href={`tel:${selected.mobile}`} className="hover:text-forest">{selected.mobile}</a>
                    </div>
                    <div className="flex items-start gap-2 text-charcoal/80">
                      <MapPin className="w-3.5 h-3.5 text-forest mt-0.5 shrink-0" />
                      <span className="whitespace-pre-wrap">{selected.address}</span>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h3 className="text-xs uppercase tracking-[0.24em] text-charcoal/50 mb-3 flex items-center gap-2">
                    <Package className="w-3 h-3" /> Line items
                  </h3>
                  <div className="border border-black/10 rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F4F5] text-charcoal/70">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Product</th>
                          <th className="text-left px-3 py-2 font-medium">Qty</th>
                          <th className="text-right px-3 py-2 font-medium">Line total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.items?.map((it, idx) => (
                          <tr key={idx} className="border-t border-black/10">
                            <td className="px-3 py-2">
                              <div className="font-medium">{it.name}</div>
                              <div className="text-xs text-charcoal/50 font-mono">
                                ₹{Number(it.unit_price).toFixed(2)}/{it.uom}
                              </div>
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{it.quantity_text}</td>
                            <td className="px-3 py-2 text-right font-mono">₹{Number(it.line_total).toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-black/20 bg-stone/20">
                          <td colSpan={2} className="px-3 py-3 font-semibold text-right">Total</td>
                          <td className="px-3 py-3 text-right font-mono font-semibold text-forest">₹{Number(selected.total).toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payment refs */}
                <div>
                  <h3 className="text-xs uppercase tracking-[0.24em] text-charcoal/50 mb-3 flex items-center gap-2">
                    <Receipt className="w-3 h-3" /> Payment refs
                  </h3>
                  <div className="text-xs font-mono space-y-1 text-charcoal/70 break-all">
                    <div>Session: {selected.session_id}</div>
                    <div>Order ID: {selected.id}</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
